import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, RotateCw, X, AlertTriangle } from "lucide-react";

export function UploadPanel({ upload }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const pct = upload.total ? Math.round((upload.done / upload.total) * 100) : 0;
  const retryable = upload.failed.filter((f) => f.file).length;

  return (
    <div className="mb-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!upload.running) upload.start(e.dataTransfer.files); }}
        className={`rounded-xl border border-dashed px-5 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-colors ${drag ? "border-[#0099CC] bg-[#0099CC]/5" : "border-[#1A2332] bg-[#0B1120]"}`}
        data-testid="upload-dropzone"
      >
        <div className="w-10 h-10 rounded-lg bg-[#0099CC]/10 flex items-center justify-center shrink-0"><Upload className="w-5 h-5 text-[#0099CC]" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium">Glissez vos photos ici ou importez-les</p>
          <p className="text-xs text-[#64748B]">JPEG, PNG, WebP — jusqu'a 50 Mo par photo. Les originaux sont conserves intacts. Apres une coupure, re-selectionnez les memes fichiers : ceux deja importes sont ignores.</p>
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={upload.running} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="upload-photos-btn">
          <Upload className="w-4 h-4 mr-2" />Importer des photos
        </Button>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="upload-input"
          onChange={(e) => { upload.start(e.target.files); e.target.value = ""; }} />
      </div>

      {(upload.running || upload.total > 0 || upload.failed.length > 0) && (
        <div className="mt-3 bg-[#0B1120] border border-[#1A2332] rounded-xl p-4" data-testid="upload-progress">
          <div className="flex items-center gap-3">
            <Progress value={pct} className="h-2 flex-1 bg-[#1A2332]" />
            <span className="text-xs font-mono text-[#0099CC] w-28 text-right" data-testid="upload-progress-count">{upload.done}/{upload.total} · {pct}%</span>
            {upload.running
              ? <Button size="sm" variant="ghost" onClick={upload.cancel} className="h-7 text-xs text-[#94A3B8]">Arreter</Button>
              : <Button size="sm" variant="ghost" onClick={upload.clear} className="h-7 text-[#64748B]"><X className="w-3.5 h-3.5" /></Button>}
          </div>
          {upload.skipped > 0 && <p className="mt-2 text-xs text-[#94A3B8]" data-testid="upload-skipped">{upload.skipped} photo(s) deja presente(s), ignoree(s)</p>}
          {upload.failed.length > 0 && (
            <div className="mt-3 space-y-1" data-testid="upload-failures">
              <p className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{upload.failed.length} fichier(s) non importe(s)</p>
              <ul className="max-h-28 overflow-y-auto text-[11px] text-[#94A3B8] space-y-0.5">
                {upload.failed.map((f, i) => <li key={i} className="truncate"><span className="text-white">{f.name}</span> — {f.error}</li>)}
              </ul>
              {retryable > 0 && !upload.running && (
                <Button size="sm" onClick={upload.retryFailed} className="h-7 text-xs bg-[#1A2332] text-white mt-1" data-testid="upload-retry-btn"><RotateCw className="w-3 h-3 mr-1" />Reessayer ({retryable})</Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
